import React, { useState } from "react";
import {
  Box,
  Grid,
  TextField,
  Typography,
  Button,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import { ITamanhoBarra } from "../types/perfil.types";

// Estilos padronizados para inputs (igual à aba GERAL)
const inputSx = {
  backgroundColor: "#f0f0f0",
  borderRadius: 0,
  "& .MuiOutlinedInput-notchedOutline": {
    border: "none",
  },
  "&:hover .MuiOutlinedInput-notchedOutline": {
    border: "none",
  },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
    border: "none",
  },
  "& .MuiInputBase-input": {
    padding: "20px 14px 8px 14px",
  },
  minHeight: 56,
};

const labelSx = {
  color: "#3C90AD",
  fontWeight: 500,
  fontSize: "0.8rem",
  "&.Mui-focused": {
    color: "#3C90AD",
  },
};

interface TabTamanhosBarrasProps {
  tamanhoBarras: ITamanhoBarra[];
  onChange: (tamanhos: ITamanhoBarra[]) => void;
  figuraUrl?: string;
  onOpenMidias: () => void;
}

export const TabTamanhosBarras: React.FC<TabTamanhosBarrasProps> = ({
  tamanhoBarras,
  onChange,
  figuraUrl,
  onOpenMidias,
}) => {
  const [itemParaRemover, setItemParaRemover] = useState<number | null>(null);

  // Verificar se existe um campo vazio (novo) ainda não preenchido
  const temCampoVazio = tamanhoBarras.some((t) => !t.comprimento || t.comprimento === 0);

  // Verificar duplicatas
  const verificarDuplicata = (comprimento: number, sequencia: number): boolean => {
    return tamanhoBarras.some(
      (t) => t.comprimento === comprimento && t.sequencia !== sequencia
    );
  };

  const handleAdicionar = () => {
    const novaSequencia = tamanhoBarras.length > 0
      ? Math.max(...tamanhoBarras.map((t) => t.sequencia)) + 1
      : 1;

    onChange([
      ...tamanhoBarras,
      { sequencia: novaSequencia, comprimento: 0 },
    ]);
  };

  const handleAlterarComprimento = (sequencia: number, valor: number) => {
    onChange(
      tamanhoBarras.map((t) =>
        t.sequencia === sequencia ? { ...t, comprimento: valor } : t
      )
    );
  };

  const handleRemover = (sequencia: number) => {
    onChange(tamanhoBarras.filter((t) => t.sequencia !== sequencia));
    setItemParaRemover(null);
  };

  return (
    <Grid container spacing={3}>
      {/* Coluna Esquerda - Imagem do Produto */}
      <Grid item xs={12} md={3}>
        {/* Container da Imagem - COM BORDA CINZA */}
        <Box
          sx={{
            border: "1px solid #e0e0e0",
            backgroundColor: "#fff",
            p: 2,
            mb: 2,
          }}
        >
          <Typography
            variant="body2"
            sx={{ color: "#333", mb: 2, fontWeight: 500 }}
          >
            Imagem do produto
          </Typography>
          
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              minHeight: 180,
              backgroundColor: "#fff",
            }}
          >
            {figuraUrl ? (
              <img
                src={figuraUrl}
                alt="Imagem do produto"
                style={{ maxWidth: "100%", maxHeight: 180, objectFit: "contain" }}
              />
            ) : (
              <Typography variant="body2" color="text.secondary">
                Nenhuma imagem
              </Typography>
            )}
          </Box>
        </Box>

        {/* Botão FORA do container da imagem */}
        <Button
          variant="contained"
          fullWidth
          onClick={onOpenMidias}
          sx={{
            backgroundColor: "#3C90AD",
            "&:hover": { backgroundColor: "#2d6f87" },
            textTransform: "none",
            py: 1.2,
          }}
        >
          GERENCIAMENTO DE MÍDIAS
        </Button>
      </Grid>

      {/* Coluna Direita - Lista de Tamanhos */}
      <Grid item xs={12} md={9}>
        {/* Botão Adicionar */}
        <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
          <Tooltip
            title={temCampoVazio ? "Preencha o campo anterior para adicionar novo" : ""}
          >
            <span>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleAdicionar}
                disabled={temCampoVazio}
                sx={{
                  backgroundColor: "#3C90AD",
                  "&:hover": { backgroundColor: "#2d6f87" },
                  "&.Mui-disabled": { backgroundColor: "#ccc" },
                }}
              >
                ADICIONAR TAMANHO
              </Button>
            </span>
          </Tooltip>
        </Box>

        {/* Lista de Tamanhos */}
        {tamanhoBarras.length === 0 ? (
          <Paper sx={{ p: 4, textAlign: "center", backgroundColor: "#fafafa" }}>
            <Typography variant="body2" color="text.secondary">
              Nenhum tamanho de barra adicionado ainda.
            </Typography>
          </Paper>
        ) : (
          <Grid container spacing={2}>
            {tamanhoBarras.map((barra) => {
              const isDuplicado = barra.comprimento > 0 && verificarDuplicata(barra.comprimento, barra.sequencia);
              
              return (
                <Grid item xs={12} sm={6} md={4} key={barra.sequencia}>
                  <Paper
                    sx={{
                      p: 1,
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      backgroundColor: "#f5f5f5",
                      border: isDuplicado ? "1px solid #f44336" : "1px solid #e0e0e0",
                    }}
                  >
                    <Box sx={{ flex: 1 }}>
                      <TextField
                        fullWidth
                        type="number"
                        label={`Barra ${barra.sequencia}`}
                        value={barra.comprimento || ""}
                        onChange={(e) =>
                          handleAlterarComprimento(barra.sequencia, parseInt(e.target.value) || 0)
                        }
                        placeholder="Ex: 4600"
                        error={isDuplicado}
                        helperText={isDuplicado ? "Valor duplicado" : ""}
                        inputProps={{ min: 1, max: 99999 }}
                        InputLabelProps={{ sx: labelSx }}
                        sx={inputSx}
                      />
                    </Box>
                    <IconButton
                      size="small"
                      onClick={() => setItemParaRemover(barra.sequencia)}
                      sx={{ color: "#f44336" }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Paper>
                </Grid>
              );
            })}
          </Grid>
        )}
      </Grid>

      {/* Modal de Confirmação de Exclusão */}
      <Dialog open={itemParaRemover !== null} onClose={() => setItemParaRemover(null)}>
        <DialogTitle>Remover tamanho de barra</DialogTitle>
        <DialogContent>
          <Typography>Tem certeza que deseja remover este tamanho de barra?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setItemParaRemover(null)}>Cancelar</Button>
          <Button
            onClick={() => itemParaRemover !== null && handleRemover(itemParaRemover)}
            color="error"
            variant="contained"
          >
            Remover
          </Button>
        </DialogActions>
      </Dialog>
    </Grid>
  );
};
